import { isServer } from '@vue-storefront/core/helpers';
import { nonReactiveState } from './index';
import Vue from 'vue'
import { MutationTree } from 'vuex'
import * as types from './mutation-types'
import CategoryState from './CategoryState'
import { Category } from '../../types/Category'
import cloneDeep from 'lodash-es/cloneDeep'
import slugifyCategories from '@vue-storefront/core/modules/catalog/helpers/slugifyCategories'

const mutations: MutationTree<CategoryState> = {
  [types.CATEGORY_SET_PRODUCTS] (state, products = []) {
    nonReactiveState.products = cloneDeep(products)
    state.products = isServer ? products : products.map(prod => prod.sku)
  },
  [types.CATEGORY_ADD_PRODUCTS] (state, products = []) {
    nonReactiveState.products.push(...cloneDeep(products))
    state.products.push(...(isServer ? products : products.map(prod => prod.sku)))
  },
  [types.CATEGORY_ADD_CATEGORY] (state, category: Category) {
    if (category) {
      Vue.set(state.categoriesMap, category.id, category)
    }
  },
  [types.CATEGORY_ADD_CATEGORIES] (state, categories: Category[] = []) {
    if (categories.length) {
      const newCategoriesEntry = {}
      categories.forEach(category => {
        newCategoriesEntry[category.id] = category
      })
      state.categoriesMap = Object.assign({}, state.categoriesMap, newCategoriesEntry)
    }
  },
  [types.CATEGORY_ADD_NOT_FOUND_CATEGORY_IDS] (state, categoryIds: string[] = []) {
    state.notFoundCategoryIds = [...state.notFoundCategoryIds, ...categoryIds]
  },
  [types.CATEGORY_SET_CATEGORY_FILTERS] (state, { category, filters }) {
    Vue.set(state.filtersMap, category.id, filters)
  },
  [types.CATEGORY_SET_SEARCH_PRODUCTS_STATS] (state, stats = {}) {
    state.searchProductsStats = stats
  },
  [types.CATEGORY_SET_MENU_INCLUDE_IN_MENU_CATALOG_REQUEST_KEY] (state, key: string | null) {
    state.menuIncludeInMenuCatalogRequestKey = key
  },
  [types.CATEGORY_ADD_MENU_CHILDREN_LOADED_PARENTS] (state, parentIds: Array<number | string> = []) {
    const next = new Set((state.menuChildrenLoadedParentIds || []).map(Number))
    for (const id of parentIds) {
      const n = Number(id)
      if (!Number.isNaN(n)) {
        next.add(n)
      }
    }
    state.menuChildrenLoadedParentIds = Array.from(next)
  },
  [types.CATEGORY_RESET_MENU_CHILDREN_LOADED_PARENTS] (state) {
    state.menuChildrenLoadedParentIds = []
  },
  [types.CATEGORY_SET_MENU_CATEGORIES] (state, { items = [] }) {
    state.menuCategories = items.map((category) => slugifyCategories(category))
    state.menuCategories.sort((catA, catB) => {
      if (catA.position && catB.position) {
        if (catA.position < catB.position) return -1
        if (catA.position > catB.position) return 1
      }
      return 0
    })
    // Full replace — parent-fetch completeness markers no longer apply.
    state.menuChildrenLoadedParentIds = []
  },
  [types.CATEGORY_UPD_MENU_CATEGORIES] (state, categories) {
    for (let category of categories.items) {
      category = slugifyCategories(category)
      const catExist = state.menuCategories.find(existingCat => existingCat.id === category.id)

      if (catExist) {
        Object.assign(catExist, category)
      } else {
        state.menuCategories.push(category)
      }
    }

    state.menuCategories.sort((catA, catB) => {
      if (catA.position && catB.position) {
        if (catA.position < catB.position) return -1
        if (catA.position > catB.position) return 1
      }
      return 0
    })
  },
  [types.CATEGORY_RESET_MENU_CATEGORIES] (state) {
    state.menuCategories = []
    state.menuIncludeInMenuCatalogRequestKey = null
    state.menuChildrenLoadedParentIds = []
  }
}

export default mutations
